import { homeNewLeadsResponseSchema, type HomeNewLeadsResponse } from "@gioia365/contracts";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { apiRequest, ApiRequestError } from "../api/http";
import { useT } from "../i18n/locale";
import { useOptionalAccess, useOptionalTab } from "../session/access";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { DUE_NOW_REFRESH_MS } from "../tasks/tasks-queries";

// Il numero dei Lead nuovi da contattare accanto a «Lead» nel menu (piano M6 D24): lo stesso «Nuovi Lead da contattare» della Home
// (`GET /api/today/new-leads`), della persona della scheda o, nella «Guarda come…», di quella osservata; per chi non ha «Oggi» (Admin
// e Super admin) il server dice 0 e il numero non c'è. Come i WhatsApp da leggere accanto a «Conversazioni» (`WhatsAppRailCount`): il
// numero si vede soltanto, le parole sono la descrizione della voce; si rilegge ogni minuto e al ritorno sulla finestra; nel contesto
// di piattaforma no, perché ogni lettura resterebbe nel registro dell'azienda. Un server che non conosce la rotta vale 0.

/** Il numero sull'icona resta stretto: oltre 99 si legge «99+», e le parole dicono il numero intero. */
const MAX_SHOWN = 99;

export function newLeadsCountQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "today", "new-leads", commercialViewOf(tab)],
    queryFn: async ({ signal }): Promise<HomeNewLeadsResponse> => {
      try {
        return await apiRequest("GET", "/api/today/new-leads", { schema: homeNewLeadsResponseSchema, tab, signal });
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404 && error.code === "not_found") return { count: 0 };
        throw error;
      }
    },
    refetchInterval: DUE_NOW_REFRESH_MS,
  });
}

export function NewLeadsRailCount({ id }: { id: string }) {
  const access = useOptionalAccess();
  const tab = useOptionalTab();
  if (!access || !tab || access.isPlatform) return null;
  return <RailCount tab={tab} id={id} />;
}

function RailCount({ tab, id }: { tab: TabContext; id: string }) {
  const t = useT();
  const count = useQuery(newLeadsCountQuery(tab)).data?.count ?? 0;
  if (count === 0) return null;
  const shown = count > MAX_SHOWN ? `${MAX_SHOWN}+` : String(count);
  return (
    <>
      <span className="g-nav__count" aria-hidden="true">
        {shown}
      </span>
      <span id={id} hidden>
        {count === 1 ? t("homeToday.newLeadsOne") : t("homeToday.newLeadsMany", { count })}
      </span>
    </>
  );
}
